import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import service, { UpsertFeeRequest } from 'Src/feature/check/checkService';
import { CheckSurchargeDef } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { FeeOrGratuity } from 'Src/feature/fees-and-gratuities/feesAndGratuitiesService';
import { useFeesAndGratuties } from 'Src/feature/fees-and-gratuities/useFeesAndGratuities';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';

interface ApplicableFee {
  fee: FeeOrGratuity;
  checkFee?: CheckSurchargeDef;
}

const sectionId = 'useCheckFeesModal';

const useCheckFeesModal = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const { route } = useRoute();
  const toast = useToast();

  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;

  const check = useCheck.query.getOne(checkId);
  const feesAndGratuities = useFeesAndGratuties.query.getAll();
  const updateFee = useCheck.mutate.updateFee();
  const updateAutoFee = useCheck.mutate.updateAutoFee();

  const [openFeeValues, setOpenFeeValues] = useState<UpsertFeeRequest | undefined>(undefined);

  const modal = {
    openFee: useModalControl({
      onClose: () => setOpenFeeValues(undefined),
    }),
  };

  const applicableFees = useMemo(() => (
    (feesAndGratuities.data ?? []).reduce<ApplicableFee[]>((acc, f) => {
      if (!f.isEnabled || f.isGratuity || f.doAutoApply) return acc;

      const checkFee = (check.data?.info.surcharges ?? []).find((cf) => cf.surchargeId === f.id);

      acc.push({ fee: f, checkFee });
      return acc;
    }, [])
  ), [check.data?.info.surcharges, feesAndGratuities.data]);

  const applicableGratuities = useMemo(() => (
    (feesAndGratuities.data ?? []).reduce<ApplicableFee[]>((acc, f) => {
      if (!f.isEnabled || !f.isGratuity || f.doAutoApply) return acc;

      const checkFee = (check.data?.info.surcharges ?? []).find((cf) => cf.surchargeId === f.id);

      acc.push({ fee: f, checkFee });
      return acc;
    }, [])
  ), [check.data?.info.surcharges, feesAndGratuities.data]);

  const applicableAutoFees = useMemo(() => (
    (feesAndGratuities.data ?? []).reduce<ApplicableFee[]>((acc, f) => {
      if (!f.isEnabled || !f.doAutoApply) return acc;

      const checkFee = (check.data?.info.surcharges ?? []).find((cf) => cf.surchargeId === f.id);
      if (!checkFee) return acc;

      acc.push({ fee: f, checkFee });
      return acc;
    }, [])
  ), [check.data?.info.surcharges, feesAndGratuities.data]);

  const handleFeeQtyChange = (qty: number, fee: FeeOrGratuity) => {
    if (typeof checkId !== 'number') return log.warn('handleFeeQtyChange', '!checkId');
    if (updateFee.isPending) return;
    if (fee.type === 'FixedAmount' || fee.type === 'FixedPercent') {
      handleFeeChange({ checkId, feeId: fee.id, quantity: qty });
      return;
    }
    const checkFee = (check.data?.info.surcharges ?? []).find((cf) => cf.surchargeId === fee.id);
    if ((!checkFee || checkFee.quantity === 0) && qty === 1) {
      setOpenFeeValues({ checkId, feeId: fee.id, quantity: qty });
      modal.openFee.open();
    } else {
      handleFeeChange({ checkId, feeId: fee.id, quantity: qty, openAmount: checkFee?.value ?? 0 });
    }
  };

  const handleGratuityChange = (fee: FeeOrGratuity) => {
    if (typeof checkId !== 'number') return log.warn('handleGratuityChange', '!checkId');
    if (updateFee.isPending) return;
    const checkFee = (check.data?.info.surcharges ?? []).find((cf) => cf.surchargeId === fee.id);
    if (!checkFee) {
      if (fee.type === 'FixedAmount' || fee.type === 'FixedPercent') {
        handleFeeChange({ checkId, feeId: fee.id, shouldApply: true });
        return;
      }
      setOpenFeeValues({ checkId, feeId: fee.id, shouldApply: true });
      modal.openFee.open();
    } else {
      if (fee.type === 'FixedAmount' || fee.type === 'FixedPercent') {
        handleFeeChange({ checkId, feeId: fee.id, shouldApply: false });
        return;
      }
      handleFeeChange({ checkId, feeId: fee.id, openAmount: checkFee.value, shouldApply: false });
    }
  };

  const handleOpenFeeConfirm = (val: number) => {
    if (updateFee.isPending || !openFeeValues) return;
    handleFeeChange({ ...openFeeValues, openAmount: val });
  };

  const handleFeeChange = (...args: Parameters<typeof service.updateFee>) => {
    updateFee.mutate(...args, {
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.checkFee-update`, title: t('checkFees.errors.updateFee') });
      },
      onSuccess: () => {
        if (modal.openFee.isOpen) {
          modal.openFee.close();
          setOpenFeeValues(undefined);
        }
      },
    });
  };

  const handleAutoFeeChange = (...args: Parameters<typeof service.updateAutoFee>) => {
    updateAutoFee.mutate(...args, {
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.checkAutoFee-update`, title: t('checkFees.errors.updateAutoFee') });
      },
    });
  };

  return {
    checkId,
    modal,
    applicableFees,
    applicableGratuities,
    applicableAutoFees,
    handleOpenFeeConfirm,
    handleFeeQtyChange,
    handleGratuityChange,
    handleFeeChange,
    handleAutoFeeChange,
    isLoading: feesAndGratuities.isLoading,
    updateFee,
    updateAutoFee,
    openFeeValues,
    setOpenFeeValues,
  };
};

export { useCheckFeesModal };
